/* eslint-disable no-const-assign */
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import './../../../assets/css/requestimates.css';
import CampaignWizard from './CampaignWizard';
import CampaignDetails from './CampaignDetails';
import { HTTPService } from "./../../../services/httpService";
import ReactGA from 'react-ga4';
import { convertFromUTC } from '../../../util/convert_timezone';

const API = new HTTPService();

class Campaigns extends Component {
  state = {
    campaigns: [],
    selectedCampaign: {},
    action: "display", // "display", "add", "edit"
    hiddenList: false,
    userType: this.props.userType,
    typeId: this.props.typeId,
    user: this.props.user,
    companyName: this.props.companyName,
    company: this.props.company,
    access: this.props.access,
    showHelpVideo: false,
  };

  // Get agent parameter from URL
  getAgentFromUrl = () => {
    const params = new URLSearchParams(this.props.location.search);
    return params.get('agent'); // Returns 'iris', 'luke', or null
  };

  // Filter campaigns based on user access and agent parameter
  filterCampaigns = (campaigns) => {
    if (!Array.isArray(campaigns)) return [];

    // Filter out deleted campaigns
    let filtered = campaigns.filter(campaign => campaign.campaign_status !== "deleted");

    // Filter by agent parameter if present
    const agent = this.getAgentFromUrl();
    if (agent === 'iris') {
      // Show only system campaigns (CX Communication)
      filtered = filtered.filter(campaign => campaign.campaign_type === "system");
    } else if (agent === 'luke') {
      // Show only always_on and promotional campaigns (remarketing)
      filtered = filtered.filter(campaign =>
        campaign.campaign_type === "always_on" || campaign.campaign_type === "promotional"
      );
    }
    // If no agent parameter, show all (default behavior)

    return filtered;
  };

  // Sort campaigns by timing
  sortCampaignsByTiming = (campaigns) => {
    if (!Array.isArray(campaigns)) return [];

    return campaigns.sort((a, b) => {
      // Parse rules_json for both campaigns
      let rulesA = {};
      let rulesB = {};

      try {
        if (a.rules_json) {
          rulesA = typeof a.rules_json === 'string' ? JSON.parse(a.rules_json) : a.rules_json;
        }
      } catch (e) {
        console.error("Error parsing rules_json for campaign A:", e);
      }

      try {
        if (b.rules_json) {
          rulesB = typeof b.rules_json === 'string' ? JSON.parse(b.rules_json) : b.rules_json;
        }
      } catch (e) {
        console.error("Error parsing rules_json for campaign B:", e);
      }

      // For system campaigns, sort by trigger_state first (created → scheduled → collected),
      // then by trigger_offset_days within each state
      if (a.campaign_type === 'system' && b.campaign_type === 'system') {
        // Define state order: created (estimates) → scheduled (jobs about to start) → collected (completed jobs)
        const stateOrder = { 'created': 1, 'scheduled': 2, 'collected': 3 };
        const stateA = rulesA.trigger_state || '';
        const stateB = rulesB.trigger_state || '';

        const orderA = stateOrder[stateA] || 999;
        const orderB = stateOrder[stateB] || 999;

        // If different states, sort by state order
        if (orderA !== orderB) {
          return orderA - orderB;
        }

        // Same state, sort by trigger_offset_days (negative first, then ascending)
        const offsetA = rulesA.trigger_offset_days || 0;
        const offsetB = rulesB.trigger_offset_days || 0;
        return offsetA - offsetB;
      }

      // For promotional campaigns, sort by start date (earliest first)
      if (a.campaign_type === 'promotional' && b.campaign_type === 'promotional') {
        const dateA = a.promotional_start_date ? new Date(a.promotional_start_date) : new Date('9999-12-31');
        const dateB = b.promotional_start_date ? new Date(b.promotional_start_date) : new Date('9999-12-31');
        return dateA - dateB;
      }

      // For always_on campaigns, sort by created_at (oldest first)
      if (a.campaign_type === 'always_on' && b.campaign_type === 'always_on') {
        const dateA = a.created_at ? new Date(a.created_at) : new Date();
        const dateB = b.created_at ? new Date(b.created_at) : new Date();
        return dateA - dateB;
      }

      // Mixed types: system < promotional < always_on
      const typeOrder = { 'system': 1, 'promotional': 2, 'always_on': 3 };
      return (typeOrder[a.campaign_type] || 999) - (typeOrder[b.campaign_type] || 999);
    });
  };

  async componentDidMount() {
    // Only installers can access campaigns
    if (this.state.userType === "installer") {
      try {
        const { data: campaigns } = await API.get(`/campaigns?installer_id=${this.state.typeId}`);
        const filteredCampaigns = this.filterCampaigns(campaigns);
        const sortedCampaigns = this.sortCampaignsByTiming(filteredCampaigns);
        this.setState({ campaigns: sortedCampaigns });
      } catch (error) {
        console.error("Error loading campaigns:", error);
        this.setState({ campaigns: [] });
      }
    }
  }

  async componentDidUpdate(prevProps) {
    // Re-filter campaigns when URL parameter changes
    if (prevProps.location.search !== this.props.location.search) {
      try {
        const { data: campaigns } = await API.get(`/campaigns?installer_id=${this.state.typeId}`);
        const filteredCampaigns = this.filterCampaigns(campaigns);
        const sortedCampaigns = this.sortCampaignsByTiming(filteredCampaigns);
        this.setState({ campaigns: sortedCampaigns });
      } catch (error) {
        console.error("Error loading campaigns:", error);
        this.setState({ campaigns: [] });
      }
    }
  }

  handleCampaignSelect = (campaign, isMobile = false) => {
    this.setState({ 
      selectedCampaign: campaign,
      action: "display",
      hiddenList: isMobile
    });
  };

  handleCreateNew = () => {
    ReactGA.event({category: 'Campaign', action: 'Create New Automation', label: 'prod'});
    this.setState({
      action: "add",
      selectedCampaign: {},
      hiddenList: true // Hide list on mobile when creating
    });
  };

  handleEdit = () => {
    console.log("Edit clicked for automation:", this.state.selectedCampaign);
    ReactGA.event({category: 'Campaign', action: 'Edit Automation', label: 'prod'});
    this.setState({ action: "edit" });
  };

  handleCancel = async () => {
    // Refresh campaign list to show any updates
    try {
      const { data: campaigns } = await API.get(`/campaigns?installer_id=${this.state.typeId}`);
      const filteredCampaigns = this.filterCampaigns(campaigns);
      const sortedCampaigns = this.sortCampaignsByTiming(filteredCampaigns);
      this.setState({
        campaigns: sortedCampaigns,
        selectedCampaign: {},
        action: "display",
        hiddenList: false
      });
    } catch (error) {
      console.error("Error refreshing campaigns:", error);
      // Still close the wizard even if refresh fails
      this.setState({
        selectedCampaign: {},
        action: "display",
        hiddenList: false
      });
    }
  };

  handleDelete = async (campaignId) => {
    try {
      await API.delete(`/campaigns/${campaignId}`, {
        installer_id: this.state.typeId
      });

      ReactGA.event({category: 'Campaign', action: 'Delete Automation', label: 'prod'});
      alert("Automation deleted.");

      // Refresh campaign list
      const { data: campaigns } = await API.get(`/campaigns?installer_id=${this.state.typeId}`);
      const filteredCampaigns = this.filterCampaigns(campaigns);
      const sortedCampaigns = this.sortCampaignsByTiming(filteredCampaigns);
      this.setState({
        campaigns: sortedCampaigns,
        selectedCampaign: {},
        action: "display"
      });
    } catch (error) {
      console.error("Error deleting automation:", error);
      alert("Error deleting automation. Please try again.");
    }
  };

  handleToggleStatus = async (campaignId, newStatus) => {
    try {
      await API.put(`/campaigns/${campaignId}`, {
        installer_id: this.state.typeId,
        campaign_status: newStatus
      });

      ReactGA.event({
        category: 'Campaign',
        action: newStatus === 'active' ? 'Turn On Automation' : 'Turn Off Automation',
        label: 'prod'
      });

      alert(`Automation is now ${newStatus === "active" ? "On" : "Off"}.`);

      // Refresh campaign list
      const { data: campaigns } = await API.get(`/campaigns?installer_id=${this.state.typeId}`);
      const filteredCampaigns = this.filterCampaigns(campaigns);
      const sortedCampaigns = this.sortCampaignsByTiming(filteredCampaigns);
      const updatedCampaign = sortedCampaigns.find(c => c.id === campaignId);

      this.setState({
        campaigns: sortedCampaigns,
        selectedCampaign: updatedCampaign || {},
        action: "display"
      });

    } catch (error) {
      console.error("Error toggling automation status:", error);
      alert("Error updating automation status. Please try again.");
    }
  };

  handleShowHelpVideo = () => {
    this.setState({ showHelpVideo: true });
  };

  handleCloseHelpVideo = () => {
    this.setState({ showHelpVideo: false });
  };

  handleSubmit = async (campaignData) => {
    try {
      if (this.state.action === "add") {
        // Create new campaign
        const payload = {
          installer_id: this.state.typeId,
          ...campaignData
        };

        await API.post('/campaigns', payload);

        ReactGA.event({category: 'Campaign', action: 'Created Automation', label: 'prod'});
        alert("Automation saved.");

      } else if (this.state.action === "edit") {
        // Update existing campaign
        const payload = {
          installer_id: this.state.typeId,
          ...campaignData
        };

        await API.put(`/campaigns/${this.state.selectedCampaign.id}`, payload);

        ReactGA.event({category: 'Campaign', action: 'Updated Automation', label: 'prod'});
        alert("Automation saved.");
      }

      // Refresh campaign list
      const { data: campaigns } = await API.get(`/campaigns?installer_id=${this.state.typeId}`);
      const filteredCampaigns = this.filterCampaigns(campaigns);
      const sortedCampaigns = this.sortCampaignsByTiming(filteredCampaigns);
      this.setState({
        campaigns: sortedCampaigns,
        selectedCampaign: {},
        action: "display",
        hiddenList: false
      });

    } catch (error) {
      console.error("Error saving automation:", error);
      alert("Error saving automation: " + (error.response?.data?.message || error.message));
    }
  };

  iconState = (status) => {
    switch (status) {
      case "active":
        return (
          <i
            className="fa-sharp fa-regular fa-toggle-on"
            style={{ color: '#28a745', fontSize: '20px' }}
            title="On">
          </i>
        );
      case "paused":
      case "draft":
        return (
          <i
            className="fa-sharp fa-regular fa-toggle-off"
            style={{ color: '#6c757d', fontSize: '20px' }}
            title="Off">
          </i>
        );
      case "completed":
        return (
          <i
            className="fa-sharp fa-regular fa-check-circle"
            style={{ color: '#17a2b8', fontSize: '20px' }}
            title="Completed">
          </i>
        );
      case "deleted":
        return (
          <i
            className="fa-sharp fa-regular fa-trash"
            style={{ color: '#dc3545', fontSize: '20px' }}
            title="Deleted">
          </i>
        );
      default:
        return (
          <i
            className="fa-sharp fa-regular fa-question-circle"
            style={{ fontSize: '20px' }}>
          </i>
        );
    }
  };

  // Get page title based on agent parameter
  getPageTitle = () => {
    const agent = this.getAgentFromUrl();
    if (agent === 'iris') {
      return "Customer Experience";
    } else if (agent === 'luke') {
      return "Re-Engagement";
    }
    return "Automations";
  };

  render() {
    const { campaigns, selectedCampaign, action, hiddenList, userType } = this.state;
    const isEmptyList = !Array.isArray(campaigns) || campaigns.length === 0;
    const namePage = this.getPageTitle();

    console.log("The logged in user is : ", this.props.user);

    // Helper function to get agent label for a campaign
    const getAgentLabel = (campaign) => {
      if (campaign.campaign_type === 'system') {
        return 'Iris – Customer Experience';
      } else if (campaign.campaign_type === 'always_on' || campaign.campaign_type === 'promotional') {
        return 'Luke – Re-Engagement';
      }
      return 'Unknown';
    };

    // Only show to installers
    if (userType !== "installer") {
      return (
        <div className="alert alert-warning m-4">
          Automations are only available for specialists.
        </div>
      );
    }

    return (
      <React.Fragment>
        {/* Mobile header */}
        <h3 className="page-name d-lg-none d-sm-block list-header">
          {namePage}
          {(Object.keys(selectedCampaign).length > 0 || action !== "display") && (
            <button type="button" className="close" aria-label="Close" onClick={this.handleCancel}>
              <span aria-hidden="true">&times;</span>
            </button>
          )}
        </h3>

        {/* DISPLAY MODE: Sidebar + Details Panel */}
        {action === "display" && (
          <React.Fragment>
            <div className="row">
              {/* LEFT SIDEBAR - Desktop */}
              <div className="col-lg-4 text-center d-none d-lg-block">
                <h5 className="page-name p-3 mb-0 corners list-header">{namePage}</h5>
                <ul className="list-group-fluid p-0 list-group-flush shadow bg-white corners">
                  <li className="list-group-item d-flex justify-content-center align-items-center corners">
                    <button
                      type="button"
                      onClick={this.handleCreateNew}
                      className="button btn-success m-2 buttons"
                    >
                      New Automation
                    </button>
                    {userType === "installer" && (
                      <button
                        type="button"
                        onClick={this.handleShowHelpVideo}
                        className="btn btn-link p-1 ml-2"
                        title="Click for help video"
                        style={{ fontSize: '16px', color: '#6c757d' }}
                      >
                        <i className="fa-sharp fa-regular fa-question-circle"></i>
                      </button>
                    )}
                  </li>

                  {!isEmptyList && campaigns.map(campaign => (
                    <li
                      onClick={() => this.handleCampaignSelect(campaign)}
                      key={campaign.id}
                      className={campaign.id === selectedCampaign.id ? "list-group-item active" : "list-group-item"}
                      style={{ cursor: 'pointer' }}
                    >
                      <small>
                        <div className="row">
                          <div className="col-2">
                            <b>{convertFromUTC(campaign.created_at).format("MMM D")}</b>
                          </div>
                          <div className="col-8">
                            <div className="row">
                              <div className="col"><strong>{campaign.campaign_name}</strong></div>
                            </div>
                            <div className="row">
                              <div className="col text-muted" style={{ fontSize: '11px' }}>
                                Runs Under: {getAgentLabel(campaign)}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col text-muted" style={{ fontSize: '11px' }}>
                                Status: {campaign.campaign_status === "active" ? "On" : campaign.campaign_status === "completed" ? "Completed" : "Off"} • Last Sent: {campaign.last_run_at ? convertFromUTC(campaign.last_run_at).fromNow() : "Never"}
                              </div>
                            </div>
                          </div>
                          <div className="col-2 text-center">
                            {this.iconState(campaign.campaign_status)}
                          </div>
                        </div>
                      </small>
                    </li>
                  ))}
                </ul>
              </div>

              {/* LEFT SIDEBAR - Mobile */}
              {hiddenList === false && (
                <div className="col-lg-4 text-center d-lg-none d-sm-block">
                  <ul className="list-group-fluid p-0 list-group-flush shadow bg-white">
                    <li className="list-group-item d-flex justify-content-center align-items-center">
                      <button
                        type="button"
                        onClick={this.handleCreateNew}
                        className="btn btn-success btn-lg mt-2 mb-2"
                      >
                        New Automation
                      </button>
                      {userType === "installer" && (
                        <button
                          type="button"
                          onClick={this.handleShowHelpVideo}
                          className="btn btn-link p-1 ml-2"
                          title="Click for help video"
                          style={{ fontSize: '16px', color: '#6c757d' }}
                        >
                          <i className="fa-sharp fa-regular fa-question-circle"></i>
                        </button>
                      )}
                    </li>

                    {!isEmptyList && campaigns.map(campaign => (
                      <li
                        onClick={() => this.handleCampaignSelect(campaign, true)}
                        key={campaign.id}
                        className={campaign.id === selectedCampaign.id ? "list-group-item active" : "list-group-item"}
                        style={{ cursor: 'pointer' }}
                      >
                        <small>
                          <div className="row">
                            <div className="col-2">
                              <b>{convertFromUTC(campaign.created_at).format("MMM D")}</b>
                            </div>
                            <div className="col-8">
                              <div className="row">
                                <div className="col"><strong>{campaign.campaign_name}</strong></div>
                              </div>
                              <div className="row">
                                <div className="col text-muted" style={{ fontSize: '11px' }}>
                                  Runs Under: {getAgentLabel(campaign)}
                                </div>
                              </div>
                              <div className="row">
                                <div className="col text-muted" style={{ fontSize: '11px' }}>
                                  Status: {campaign.campaign_status === "active" ? "On" : campaign.campaign_status === "completed" ? "Completed" : "Off"} • Last Sent: {campaign.last_run_at ? convertFromUTC(campaign.last_run_at).fromNow() : "Never"}
                                </div>
                              </div>
                            </div>
                            <div className="col-2 text-center">
                              {this.iconState(campaign.campaign_status)}
                            </div>
                          </div>
                        </small>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* RIGHT PANEL - Mobile: only show when selected. Desktop: always show */}
              <div className={Object.keys(selectedCampaign).length === 0 ? "col-lg-8 d-none d-lg-block" : "col-lg-8"}>
                {/* When nothing selected, just show the message */}
                {Object.keys(selectedCampaign).length === 0 && (
                  <h5 className="text-center p-3">
                    {isEmptyList
                      ? 'To create a new automation, click the "New Automation" button'
                      : "Select an automation to view details or create a new one."
                    }
                  </h5>
                )}

                {/* When something is selected, show full detail panel */}
                {Object.keys(selectedCampaign).length > 0 && (
                  <CampaignDetails
                    campaign={selectedCampaign}
                    installerId={this.state.typeId}
                    installerEmail={this.state.company?.installer_email}
                    onEdit={this.handleEdit}
                    onDelete={this.handleDelete}
                    onToggleStatus={this.handleToggleStatus}
                    onCancel={this.handleCancel}
                  />
                )}
              </div>
            </div>
          </React.Fragment>
        )}

        {/* WIZARD MODE: Full-Width Campaign Wizard */}
{(action === "add" || action === "edit") && (
  <div className="row">
    <div className="col-lg-12">
      <CampaignWizard
        mode={action}
        campaign={action === "edit" ? selectedCampaign : null}
        typeId={this.state.typeId}
        user={this.state.user}
        company={this.state.company}
        agentType={this.getAgentFromUrl()}
        onSubmit={this.handleSubmit}
        onCancel={this.handleCancel}
      />
    </div>
  </div>
)}

        {/* Help Video Modal - Desktop */}
        {this.state.showHelpVideo && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
            }}
            onClick={this.handleCloseHelpVideo}
            className="d-none d-lg-flex"
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '20px',
                maxWidth: '90%',
                maxHeight: '90%',
                display: 'flex',
                flexDirection: 'column',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h5 style={{ margin: 0 }}>How to Add, Edit, and Activate Automations</h5>
                <button
                  type="button"
                  onClick={this.handleCloseHelpVideo}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    padding: 0,
                    marginLeft: '20px'
                  }}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
              <div style={{ width: '100%', aspectRatio: '16 / 12', minHeight: '480px' }}>
                <iframe
                  src="https://scribehow.com/embed/Add_Edit_and_Activate_Automations_for_Customer_Experience_AND_Re-Marketing__pLr4FOWSSJSQGWr2vvo2Rw?as=video"
                  width="100%"
                  height="100%"
                  allowFullScreen
                  style={{ border: 0 }}
                  title="How to Add, Edit, and Activate Automations"
                />
              </div>
            </div>
          </div>
        )}

        {/* Help Video Modal - Mobile */}
        {this.state.showHelpVideo && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'white',
              zIndex: 9999,
              display: 'flex',
              flexDirection: 'column',
            }}
            className="d-lg-none"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px', borderBottom: '1px solid #ddd' }}>
              <h5 style={{ margin: 0, fontSize: '16px' }}>Automations Help</h5>
              <button
                type="button"
                onClick={this.handleCloseHelpVideo}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: 0
                }}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div style={{ flex: 1, backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
              <iframe
                src="https://scribehow.com/embed/Add_Edit_and_Activate_Automations_for_Customer_Experience_AND_Re-Marketing__pLr4FOWSSJSQGWr2vvo2Rw?as=video"
                width="100%"
                height="100%"
                allowFullScreen
                style={{ border: 0 }}
                title="How to Add, Edit, and Activate Automations"
              />
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }
}

export default withRouter(Campaigns);